import { Wrench } from "lucide-react";
import type { DetailedHelpMap } from "./types";

/**
 * Ajuda detalhada: Governança, Manutenções. Conteúdo verificado contra o
 * código real — nunca invente um campo/regra sem confirmar no componente/
 * controller correspondente.
 */
export const GESTAO_OPERACIONAL_HELP: DetailedHelpMap = {
  "/governanca": [
    {
      title: "O que esta tela faz",
      description:
        "É o painel da equipe de limpeza. Cada quarto tem um estado de limpeza, e é aqui que ele anda: sujo depois que o hóspede sai, em limpeza enquanto a camareira trabalha, inspecionado quando alguém confere, limpo quando está pronto para receber.",
      items: [
        {
          label: "Como um quarto vira 'Sujo' sozinho",
          explanation:
            "Você não precisa marcar na mão: quando o hóspede faz check-out, o sistema já joga o quarto para Sujo. Esse é o aviso automático para a governança.",
          fact: (ctx) => {
            const { rooms_dirty: sujos, rooms_cleaning: limpando, rooms_total: total } = ctx.screen;
            if (sujos === undefined || total === undefined) return null;
            if (sujos === 0 && limpando === 0) return `nenhum quarto sujo ou em limpeza agora (de ${total} quartos).`;
            return `${sujos} quarto(s) sujo(s) e ${limpando ?? 0} em limpeza, de ${total} no total.`;
          },
        },
        {
          label: "Cards de resumo no topo",
          explanation:
            "Contam quantos quartos estão em cada estado neste momento. Servem para a governança saber onde está o gargalo do dia sem abrir quarto por quarto.",
        },
        {
          label: "Botões de estado no card do quarto",
          explanation:
            "Movem aquele quarto para o próximo passo da limpeza. É a mesma informação que a recepção vê na hora de alocar um hóspede — por isso vale manter atualizado durante o turno, não só no fim.",
        },
        {
          label: "Botão de manutenção no card",
          visual: { icon: Wrench, text: "Manutenção", tone: "danger" },
          explanation:
            "Abre uma ordem de manutenção para aquele quarto (o que está quebrado, previsão de liberação). Enquanto a ordem estiver aberta, o quarto fica bloqueado no Mapa de Ocupação e ninguém consegue alocar hóspede nele.",
          fact: (ctx) => {
            const fora = ctx.screen.rooms_out_of_order;
            if (fora === undefined) return null;
            return fora === 0
              ? "nenhum quarto fora de operação agora."
              : `${fora} quarto(s) marcado(s) como fora de operação — eles não aparecem como disponíveis para reserva.`;
          },
        },
        {
          label: "Ícone de inspeção",
          explanation:
            "Abre os checklists de inspeção cadastrados para rodar naquele quarto (cama, banheiro, frigobar...). Os modelos de checklist são criados pela administração da plataforma.",
        },
      ],
    },
  ],
  "/manutencoes": [
    {
      title: "O que esta tela faz",
      description:
        "Lista as ordens de manutenção do hotel. Uma ordem aberta tira o quarto de circulação: ele fica bloqueado no Mapa de Ocupação até alguém concluir o reparo — é o que impede vender um quarto quebrado.",
      items: [
        {
          label: "Nova manutenção",
          explanation:
            "Registra o problema num quarto: motivo, data de início e previsão de liberação. A partir daí o quarto sai da disponibilidade.",
          fact: (ctx) => {
            const bloqueando = ctx.screen.maintenances_blocking;
            if (bloqueando === undefined) return null;
            return bloqueando === 0
              ? "nenhuma ordem bloqueando quarto agora."
              : `${bloqueando} ordem(ns) aberta(s) bloqueando quarto neste momento.`;
          },
        },
        {
          label: "Iniciar / Concluir",
          explanation:
            "Avançam a ordem. Concluir pede as notas do que foi feito e devolve o quarto para a operação — enquanto não concluir, ele continua bloqueado mesmo que o reparo já tenha acabado.",
        },
        {
          label: "Estender prazo",
          explanation:
            "Empurra a previsão de liberação quando o reparo atrasa, sem precisar cancelar e recriar a ordem (o que perderia o histórico).",
        },
        {
          label: "Filtros por situação",
          explanation:
            "Todas, Agendadas, Em Andamento, Concluídas e Canceladas. Só as que ainda não terminaram é que seguram o quarto.",
        },
      ],
    },
  ],
};
